import { CanvasFileReview } from "src/pages/projects/library/canvas/canvas-review-controls";
import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Box,
  chakra,
  Column,
  type ColumnProps,
  FavoritedIcon,
  FileIcon,
  HtImage,
  HtVideo,
  MotionBox,
  motionDurations,
  motionEasings,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import { useStoreApi, type NodeProps } from "reactflow";
import {
  CANVAS_DOC_ASPECT_RATIO,
  CANVAS_EMAIL_FALLBACK_HEIGHT,
  CANVAS_EMAIL_MAX_HEIGHT,
  CANVAS_NODE_WIDTH,
} from "@hightouch/lib/llm/canvas/constants";
import type { AmpLibraryFileFragment } from "src/graphql";
import { PIN_SIZE } from "src/components/comments/pin";
import {
  fitToContentStyle,
  resolveContentHeight,
} from "src/components/preview-canvas/email-preview-iframe";
import {
  AdAssetPreview,
  getAdAssetKind,
} from "src/pages/ad-studio/components/ad-asset-preview";
import { WebPagePreview } from "src/pages/ad-studio/components/web-page-preview";
import {
  AD_TILE_IMAGE_WIDTH,
  useCanvasAd,
  useCanvasAdPreview,
} from "src/pages/projects/library/canvas/canvas-ads";
import {
  CommentFileProvider,
  toCommentFile,
} from "src/components/comments/comment-file";
import { FileCommentStack } from "src/components/comments/file-comment-stack";
import { textShimmer } from "src/components/llm/components/loading";
import {
  type CanvasCommentThread,
  useCanvasComments,
} from "src/pages/projects/library/canvas/canvas-comment-threads";
import { useRefocusAfterCommentsClose } from "src/pages/projects/library/canvas/use-refocus-after-comments-close";
import { CanvasRenameField } from "src/pages/projects/library/canvas/canvas-rename-field";
import {
  shownName,
  useCanvasRename,
} from "src/pages/projects/library/canvas/canvas-rename";
import {
  fileCommentSubject,
  getFileType,
} from "src/pages/projects/library/files/file-type";
import { isMediaUpload, isVideoUpload } from "@hightouch/lib/llm/files/types";
import { DocMiniature } from "src/pages/projects/library/files/doc-miniature";
import {
  CANVAS_ZOOM_TARGET,
  CANVAS_ZOOM_VAR,
  screenPx,
  writeCardZoom,
} from "src/pages/projects/library/canvas/screen-px";
import { DynamicContentBadge } from "src/pages/projects/library/canvas/dynamic-content-badge";
import { UploadBadge } from "src/pages/projects/library/canvas/upload-badge";
import { ContentCrossfade } from "src/pages/projects/library/canvas/content-crossfade";
import {
  AGENT_WORK_STATUS_LABELS,
  type AgentWorkStatus,
  agentWorkStatusOf,
  ChatWorkedNodesContext,
  isAgentWorkingOn,
} from "src/pages/projects/library/canvas/canvas-agent-work";
import {
  WorkingSheen,
  WorkingTextSheen,
} from "src/pages/projects/library/canvas/working-sheen";
import { ARRIVAL_RISE } from "src/pages/projects/library/canvas/use-canvas-arrivals";
import { useSlideComments } from "src/pages/projects/library/files/use-slide-comments";
import {
  SlidesPreview,
  type SlidesPreviewProps,
} from "src/pages/projects/library/files/slides-preview";
import {
  EMAIL_TILE_IMAGE_WIDTH,
  useResizedEmailHtml,
} from "src/pages/projects/library/canvas/resized-email-images";
import {
  EMAIL_ENVELOPE_ATTRIBUTE,
  EMAIL_HEIGHT_ATTRIBUTE,
} from "src/pages/projects/library/canvas/email-preview-height";
import { computeDesktopPreviewWidthFromHtml } from "src/pages/lifecycle-studio/emails/email-editor/canvas/desktop-preview-width";
import { PHONE_FRAME_RADIUS } from "src/pages/lifecycle-studio/copy/components/preview/phone-frame";
import { IosPreview } from "src/pages/lifecycle-studio/copy/components/preview/push-preview";
import { SmsPreview } from "src/pages/lifecycle-studio/copy/components/preview/sms-preview";
import { MESSAGE_PREVIEW_NODE_WIDTH } from "src/pages/lifecycle-studio/copy/dynamic-content/message-preview-constants";
import { hasPlatformModifier } from "src/utils/platform";
import { NAMES_OFF_ATTR } from "src/pages/projects/library/canvas/canvas-name-floor";

/** A web page tile draws its 1280px page at 288px, so a full-width image at the top zoom on a 2x screen lands near this many pixels. */
const WEB_PAGE_IMAGE_WIDTH = 1200;
/** Canvas units a press travels before React Flow treats it as a drag rather than a click. */
export const NODE_DRAG_THRESHOLD = 5;
const DOC_ASPECT_RATIO = `1 / ${CANVAS_DOC_ASPECT_RATIO}`;
/** The selection ring in screen px at any zoom: a white gap, then the accent; the white keeps it readable on a white ad. */
const SELECTION_RING = `0 0 0 ${screenPx(1.5)} var(--chakra-colors-white), 0 0 0 ${screenPx(3)} var(--chakra-colors-primary-base)`;
/** The hover hairline: one screen px of ink at 16%. */
const HOVER_HAIRLINE = `0 0 0 ${screenPx(1)} var(--chakra-colors-blackAlpha-16)`;
/** SMS and push lay out at the width Lifecycle Studio's editor draws them, then scale to fill the node. */
const MESSAGE_SCALE = {
  sms: CANVAS_NODE_WIDTH / MESSAGE_PREVIEW_NODE_WIDTH.sms,
  push: CANVAS_NODE_WIDTH / MESSAGE_PREVIEW_NODE_WIDTH.push,
};
/** Line widths for a generating document, so its skeleton reads as prose. */
const DOC_LINE_WIDTHS = [
  "100%",
  "92%",
  "96%",
  "70%",
  "100%",
  "88%",
  "94%",
  "60%",
];

export type FileNodeData = {
  canvasId: string;
  /** The canvas's brand, shown as an email's sender. */
  sender: string | null;
  removed: (nodeId: string) => void;
  deleted: (fileId: string) => void;
  /** Shows a file's favorite on every node with it, ahead of the refetch. */
  favorited: (fileId: string, favorite: boolean) => void;
  file: AmpLibraryFileFragment;
  revision: number;
  /** New since the canvas loaded, so the card fades and rises in as it mounts. */
  isArrival?: boolean;
  activate: (shiftKey: boolean) => void;
  doubleClick: () => void;
  openInNewTab: () => void;
};

type PressEvent = {
  button: number;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
};

type TileWorkProps = {
  nodeId: string;
  file: AmpLibraryFileFragment;
  threads: readonly CanvasCommentThread[];
};

/** A file on the canvas is its own object: no card around it, its name floating above it. */
export function CanvasFileNode({
  id,
  data,
  selected,
  dragging,
}: NodeProps<FileNodeData>) {
  const [isHovering, setIsHovering] = useState(false);
  const rename = useCanvasRename();
  const name =
    shownName(data.file, rename.pendingNames.get(data.file.id)) || "Untitled";
  const type = getFileType(data.file);
  const isAd = data.file.type === "ad";
  const isVideo = data.file.upload ? isVideoUpload(data.file.upload) : false;
  const drawnMessage = messageDrawnBare(data.file);
  const bodyRadius =
    drawnMessage?.channel === "sms"
      ? `${PHONE_FRAME_RADIUS * MESSAGE_SCALE.sms}px`
      : isAd
        ? "sm"
        : "2xl";
  const commentSubject = fileCommentSubject(data.file);
  // The canvas holds which tile's comments are open, so it can open one on a thread picked from its list.
  const canvasComments = useCanvasComments();
  const fileId = data.file.id;
  const openTile =
    canvasComments.openTile?.fileId === fileId ? canvasComments.openTile : null;
  const slideComments = useSlideComments(data.file);
  const fileThreads = canvasComments.threadsFor(fileId);
  const selectedAnchor = fileThreads.find(
    (thread) => thread.id === openTile?.threadId,
  )?.anchor;
  const selectSlideThread = slideComments.selectThread;
  useEffect(() => {
    if (openTile?.threadId) selectSlideThread(selectedAnchor);
  }, [selectedAnchor, openTile, selectSlideThread]);
  const showsCommentStack =
    openTile !== null || canvasComments.threadsFor(fileId).length > 0;
  const setAreCommentsOpen = (isOpen: boolean) =>
    canvasComments.setOpenTile(isOpen ? { fileId, threadId: null } : null);
  const isRenaming = rename.nodeId === id;
  // A selected or renaming card keeps its name past the floor, since that is the card in hand.
  const namesOff = selected || isRenaming ? undefined : `[${NAMES_OFF_ATTR}] &`;
  const nameButton = useRef<HTMLButtonElement>(null);
  const wasRenaming = useRef(false);
  // Focus goes back to the name when the field closes, unless a click away already moved it.
  useEffect(() => {
    if (isRenaming) {
      wasRenaming.current = true;
      return;
    }
    if (!wasRenaming.current) return;
    wasRenaming.current = false;
    if (document.activeElement === document.body) nameButton.current?.focus();
  }, [isRenaming]);
  const refocusAfterCommentsClose = useRefocusAfterCommentsClose(
    fileId,
    nameButton,
  );
  const flowStore = useStoreApi();
  const press = useRef<{
    button: number;
    x: number;
    y: number;
    farthest: number;
    dragged: boolean;
  } | null>(null);
  useEffect(() => {
    if (dragging && press.current) press.current.dragged = true;
  }, [dragging]);
  const pressCard = (event: PressEvent) => {
    // Cmd-click and a middle click open the file in a new tab, as they would a link.
    if (event.button === 1 || hasPlatformModifier(event)) data.openInNewTab();
    else data.activate(event.shiftKey);
  };
  // Read once as the card mounts, since switching the root later would remount the card.
  const [isArrival] = useState(() => Boolean(data.isArrival));
  const body = useRef<HTMLDivElement>(null);
  // The selection ring is sized in screen px, so a card selected without a hover (marquee, select-all) draws it at the current zoom.
  useLayoutEffect(() => {
    if (selected)
      writeCardZoom(body.current, flowStore.getState().transform[2]);
  }, [selected, flowStore]);
  return (
    <CardRoot
      isArrival={isArrival}
      data-group
      onMouseEnter={(event) => {
        writeCardZoom(event.currentTarget, flowStore.getState().transform[2]);
        setIsHovering(true);
      }}
      onMouseLeave={() => setIsHovering(false)}
      // A keyboard reveal shows screen-sized controls too, so focus hands the card the zoom as a hover does.
      onFocus={(event) =>
        writeCardZoom(event.currentTarget, flowStore.getState().transform[2])
      }
      position="relative"
      isolation="isolate"
      width={72}
      gap={1.5}
    >
      <Row
        height={6}
        gap={1.5}
        align="center"
        minWidth={0}
        sx={namesOff ? { [namesOff]: { visibility: "hidden" } } : undefined}
      >
        <Box
          as={type.icon}
          boxSize={3.5}
          flexShrink={0}
          color="text.tertiary"
          aria-hidden
        />
        {isRenaming && (
          <Box className="nodrag nopan" flex={1} minWidth={0}>
            <CanvasRenameField nodeId={id} file={data.file} />
          </Box>
        )}
        <Box
          ref={nameButton}
          as="button"
          type="button"
          data-card-target
          display={isRenaming ? "none" : undefined}
          flex={1}
          minWidth={0}
          textAlign="left"
          aria-pressed={selected}
          sx={
            // The button stays: its overlay is the card's click and focus target, and its text is the card's name.
            namesOff ? { [namesOff]: { visibility: "visible" } } : undefined
          }
          onPointerDown={(event) => {
            press.current =
              event.button === 0 || event.button === 1
                ? {
                    button: event.button,
                    x: event.clientX,
                    y: event.clientY,
                    farthest: 0,
                    dragged: false,
                  }
                : null;
          }}
          onPointerMove={(event) => {
            const start = press.current;
            if (!start) return;
            start.farthest = Math.max(
              start.farthest,
              Math.hypot(event.clientX - start.x, event.clientY - start.y),
            );
          }}
          onPointerCancel={() => {
            press.current = null;
          }}
          // A pointer press acts on its release: React Flow swallows the click of any press that moves at all, even a pixel.
          onPointerUp={(event) => {
            const start = press.current;
            press.current = null;
            if (!start || event.button !== start.button) return;
            // The card follows the pointer while dragged, so the release lands back on this button.
            const travelled = Math.max(
              start.farthest,
              Math.hypot(event.clientX - start.x, event.clientY - start.y),
            );
            // React Flow's threshold is in canvas units, so on screen it scales with zoom.
            const wasDragged =
              start.dragged ||
              travelled >
                NODE_DRAG_THRESHOLD * flowStore.getState().transform[2];
            if (!wasDragged) pressCard(event);
          }}
          onClick={(event) => {
            event.stopPropagation();
            // Only a keyboard or assistive click; a pointer press already acted on its release.
            if (event.detail === 0) pressCard(event);
          }}
          onDoubleClick={(event) => {
            event.stopPropagation();
            data.doubleClick();
          }}
          _after={{ content: '""', position: "absolute", inset: 0, zIndex: 0 }}
          _focusVisible={{
            // The ring goes round the whole card; the browser's own would frame the name alone, like a text field.
            outline: "none",
            _after: { boxShadow: "outline", borderRadius: "lg" },
          }}
        >
          <Box
            className="nodrag"
            position="relative"
            zIndex={1}
            // Opacity, not visibility, so the name stays the button's accessible name.
            sx={
              namesOff
                ? { [namesOff]: { opacity: 0, pointerEvents: "none" } }
                : undefined
            }
          >
            <TileName
              nodeId={id}
              file={data.file}
              threads={fileThreads}
              name={name}
              isSelected={selected}
            />
          </Box>
        </Box>
        <CanvasFileReview file={data.file} isRevealed={selected} />
        <UploadBadge file={data.file} />
        <DynamicContentBadge emailId={data.file.email?.emailId} />
        {data.file.isFavorite && (
          <Box
            as={FavoritedIcon}
            role="img"
            aria-label="Favorite"
            boxSize={4}
            flexShrink={0}
            color="red.500"
          />
        )}
      </Row>
      {/* Lets clicks through to the name button's full-card target underneath; only the comment stack takes them. */}
      <Box
        position="relative"
        pointerEvents="none"
        // The hover hairline and the selection ring each sit on a layer of their own, outside the body's clip, and only their opacity moves.
        _after={{
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: 1,
          borderRadius: bodyRadius,
          pointerEvents: "none",
          boxShadow: HOVER_HAIRLINE,
          opacity: 0,
          transitionProperty: "opacity",
          transitionDuration: "fast",
          transitionTimingFunction: "easeOutQuad",
        }}
        // The hairline would paint over the ring's white gap, so a selected card hides it.
        _groupHover={{ _after: { opacity: selected ? 0 : 1 } }}
        // A drag lifts the card: the lifted shadow sits on a layer of its own and fades in, as box-shadow itself never animates.
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          zIndex: -1,
          borderRadius: bodyRadius,
          boxShadow: "lg",
          opacity: dragging ? 1 : 0,
          transitionProperty: "opacity",
          transitionDuration: dragging ? "fast" : "faster",
          transitionTimingFunction: "easeOutQuad",
          _mediaReduceMotion: { transitionDuration: "0s" },
        }}
      >
        <Box
          ref={body}
          data-node-body
          // A message draws its own frame and shadow, so its body only shapes the ring.
          overflow={drawnMessage ? "visible" : "hidden"}
          borderRadius={bodyRadius}
          boxShadow={drawnMessage ? "none" : "md"}
          position="relative"
        >
          <ContentCrossfade file={data.file}>
            <NodeBody
              file={data.file}
              sender={data.sender}
              slideControls={slideComments.preview}
              // Only ads and videos play on hover. Any other preview would re-render for nothing.
              isHovering={(isAd || isVideo) && isHovering}
            />
          </ContentCrossfade>
          {/* A message draws its own bubble outside the body's clip, which a sheen would overrun. */}
          {!drawnMessage && (
            <WorkingTileSheen
              nodeId={id}
              file={data.file}
              threads={fileThreads}
            />
          )}
        </Box>
        <Box
          aria-hidden
          position="absolute"
          inset={0}
          zIndex={1}
          borderRadius={bodyRadius}
          pointerEvents="none"
          boxShadow={SELECTION_RING}
          opacity={selected ? 1 : 0}
          transitionProperty="opacity"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
        />
        {commentSubject && (
          <Box
            className="nodrag"
            position="absolute"
            top={`-${PIN_SIZE / 2}px`}
            right={`-${PIN_SIZE / 2}px`}
            zIndex={2}
            pointerEvents="auto"
            // Counter-scaled about its own centre, which sits on the card's corner, so the stack stays one size at any zoom.
            transformOrigin="center"
            transform={`scale(calc(1 / var(${CANVAS_ZOOM_VAR}, 1)))`}
            // Only a stack that draws something takes the zoom, which the subscriber writes to every target on each zoom frame; set here for the zoom it mounts at.
            {...(showsCommentStack ? { [CANVAS_ZOOM_TARGET]: "" } : {})}
            ref={(element: HTMLDivElement | null) => {
              if (showsCommentStack)
                element?.style.setProperty(
                  CANVAS_ZOOM_VAR,
                  String(flowStore.getState().transform[2]),
                );
            }}
          >
            <CommentFileProvider
              value={toCommentFile(data.file, canvasComments.brandProfileId)}
            >
              <FileCommentStack
                strategy="fixed"
                subject={commentSubject}
                isOpen={openTile !== null}
                onOpenChange={setAreCommentsOpen}
                openRequest={openTile}
                threads={fileThreads}
                newCommentAnchor={slideComments.anchor}
                newCommentLabel={slideComments.label}
                composerKey={slideComments.composerKey}
                canCreate={slideComments.canComment}
                threadSubtitle={slideComments.labelForThread}
                onSelectThread={(threadId) =>
                  canvasComments.setOpenTile({ fileId, threadId })
                }
                onCloseComplete={refocusAfterCommentsClose}
              />
            </CommentFileProvider>
          </Box>
        )}
      </Box>
    </CardRoot>
  );
}

/** What an agent is doing to a tile's file, read only in the parts that show it, so a change to the chat's work re-renders those alone. */
function useAgentWorkStatus({
  nodeId,
  file,
  threads,
}: TileWorkProps): AgentWorkStatus | null {
  const isChatWorking = useContext(ChatWorkedNodesContext).has(nodeId);
  const { ad } = useCanvasAd(file.ad?.assetId);
  return agentWorkStatusOf(
    file,
    isChatWorking || isAgentWorkingOn(file, threads),
    ad,
  );
}

/** The tile's name, dimmed with a band of light passing along it while an agent works, which its tooltip names. */
function TileName({
  name,
  isSelected,
  ...work
}: TileWorkProps & { name: string; isSelected: boolean }) {
  const agentWorkStatus = useAgentWorkStatus(work);
  return (
    <Box
      position="relative"
      // As wide as the name, so the band sweeps the name and not the empty row after it.
      width="fit-content"
      maxWidth="full"
    >
      <TextWithTooltip
        size="sm"
        color={
          agentWorkStatus
            ? textShimmer.color
            : isSelected
              ? "text.primary"
              : "text.secondary"
        }
        // What the agent is doing goes in the name's tooltip, as a label beside it would crowd out the review tag.
        message={
          agentWorkStatus
            ? AGENT_WORK_STATUS_LABELS[agentWorkStatus]
            : undefined
        }
        forceTooltip={agentWorkStatus !== null}
        // Below the name, as the item bar takes the space above a selected tile.
        placement="bottom-start"
      >
        {name}
      </TextWithTooltip>
      {/* Readable at any zoom, where the dots lit round the tile may be too few to see. */}
      {agentWorkStatus && (
        <WorkingTextSheen size="sm" color={textShimmer.shimmerColor}>
          {name}
        </WorkingTextSheen>
      )}
    </Box>
  );
}

/** A faint sheen over a tile an agent is at work on. A file still generating shows the sheen on its skeleton instead. */
function WorkingTileSheen(props: TileWorkProps) {
  const agentWorkStatus = useAgentWorkStatus(props);
  return agentWorkStatus === "working" || agentWorkStatus === "decomposing" ? (
    <WorkingSheen isFaint />
  ) : null;
}

const ARRIVAL_MOTION = {
  initial: { opacity: 0, transform: `translateY(${ARRIVAL_RISE}px)` },
  // A transform string runs on the compositor; ending at "none" keeps the card from trapping fixed popovers.
  animate: { opacity: 1, transform: "none" },
  transition: {
    duration: motionDurations.slow,
    ease: motionEasings.easeOutQuart,
  },
};

/** A card that arrived after the canvas loaded fades and rises in; every other card is a plain column. */
function CardRoot({
  isArrival,
  ...props
}: Pick<
  ColumnProps,
  | "children"
  | "position"
  | "isolation"
  | "width"
  | "gap"
  | "onMouseEnter"
  | "onMouseLeave"
  | "onFocus"
> & { isArrival: boolean; "data-group"?: boolean }) {
  if (!isArrival) return <Column {...props} />;
  return (
    <MotionBox
      display="flex"
      flexDirection="column"
      {...ARRIVAL_MOTION}
      {...props}
    />
  );
}

function NodeBodyContent({
  file,
  sender,
  emailHeight,
  emailViewportWidth,
  isHovering = false,
  slideControls,
}: {
  file: AmpLibraryFileFragment;
  sender: string | null;
  /** An email's height as another view of it already measured, so this one starts at it rather than the fallback. */
  emailHeight?: number;
  /** The editor's desktop width for the email, when the caller already worked it out. */
  emailViewportWidth?: number;
  isHovering?: boolean;
  slideControls?: Omit<SlidesPreviewProps, "file">;
}) {
  const { preview, isLoading: isAdLoading } = useCanvasAdPreview(file);
  if (file.type === "slides")
    return <SlidesPreview key={file.id} file={file} {...slideControls} />;
  if (file.generationStatus === "generating")
    return <FileSkeleton type={file.type} label="Generating…" />;
  if (file.generationStatus === "failed")
    return <Placeholder>This file could not be generated.</Placeholder>;
  const drawnMessage = messageDrawnBare(file);
  if (drawnMessage) {
    const { channel, message } = drawnMessage;
    return (
      // zoom, unlike a transform, also scales the size the node lays out and measures.
      <Box
        width={`${MESSAGE_PREVIEW_NODE_WIDTH[channel]}px`}
        sx={{ zoom: MESSAGE_SCALE[channel] }}
      >
        {channel === "sms" ? (
          <SmsPreview body={message.body ?? undefined} />
        ) : (
          <IosPreview
            title={message.title ?? undefined}
            body={message.body ?? undefined}
          />
        )}
      </Box>
    );
  }
  if (file.html)
    return (
      <WebPagePreview
        content={file.html}
        versionId={file.versionId}
        variant="page"
        imageWidth={WEB_PAGE_IMAGE_WIDTH}
      />
    );
  if (file.email)
    return (
      <Column>
        <EmailEnvelope
          sender={sender}
          subject={file.email.subject}
          preheader={file.email.preheader}
        />
        {file.email.html && (
          <EmailBody
            title={file.name || "Email"}
            html={file.email.html}
            measuredHeight={emailHeight}
            viewportWidth={emailViewportWidth}
          />
        )}
      </Column>
    );
  if (file.type === "doc")
    return (
      <Box bg="white" overflow="hidden" sx={{ aspectRatio: DOC_ASPECT_RATIO }}>
        <DocMiniature file={file} width={CANVAS_NODE_WIDTH} />
      </Box>
    );
  // Until the canvas has read the ad, its preview may show an older version.
  if (isAdLoading) return <FileSkeleton type="ad" label="Loading…" />;
  if (getAdAssetKind(preview) !== "empty")
    return (
      <AdAssetPreview
        asset={preview}
        variant="thumbnail"
        isHovering={isHovering}
        thumbnailImageWidth={AD_TILE_IMAGE_WIDTH}
      />
    );
  const upload = file.upload;
  // The upload's type picks the element, because a signed URL can lack a video extension.
  if (upload && isMediaUpload(upload))
    return isVideoUpload(upload) ? (
      <HtVideo
        src={upload.downloadUrl}
        alt={file.name || ""}
        width="100%"
        display="block"
        draggable={false}
        playback={isHovering ? "autoplay" : "none"}
      />
    ) : (
      <HtImage
        src={upload.downloadUrl}
        alt={file.name || ""}
        width="100%"
        display="block"
        draggable={false}
      />
    );
  return (
    <Column bg="white" align="center" gap={2} px={4} py={8}>
      <Box as={FileIcon} boxSize={8} color="text.tertiary" />
      <Box maxWidth="100%">
        <Text size="sm" color="text.secondary" isTruncated>
          {file.upload?.filename ?? "No preview available"}
        </Text>
      </Box>
    </Column>
  );
}

/** Memoized so a tile's own re-renders (hover, selection, comments) leave its preview alone. */
export const NodeBody = memo(NodeBodyContent);

/** A built SMS or push, which the node shows as Lifecycle Studio's editor draws it rather than inside a card. */
function messageDrawnBare(file: AmpLibraryFileFragment) {
  if (
    !file.message ||
    file.generationStatus === "generating" ||
    file.generationStatus === "failed"
  )
    return null;
  if (file.type !== "sms" && file.type !== "push") return null;
  return { channel: file.type, message: file.message };
}

/** What an inbox shows above the message: who it's from, the subject and the preview text. */
function EmailEnvelope({
  sender,
  subject,
  preheader,
}: {
  sender: string | null;
  subject: string | null;
  preheader: string | null;
}) {
  return (
    <Row
      // An opening email's flight slides this out of the card as the editor's own subject and preheader rows fade in.
      {...{ [EMAIL_ENVELOPE_ATTRIBUTE]: true }}
      bg="white"
      gap={2.5}
      px={3.5}
      py={3}
      align="flex-start"
      borderBottomWidth="hairline"
      borderColor="base.divider"
    >
      {sender && (
        <Row
          boxSize={7}
          flexShrink={0}
          align="center"
          justify="center"
          borderRadius="full"
          bg="text.primary"
          aria-hidden
        >
          <Text size="sm" fontWeight="medium" color="white">
            {sender.charAt(0).toUpperCase()}
          </Text>
        </Row>
      )}
      <Column minWidth={0} flex={1} gap={0.5}>
        {sender && (
          <Text size="sm" fontWeight="semibold" isTruncated>
            {sender}
          </Text>
        )}
        <Box noOfLines={2}>
          <Text size="sm" fontWeight="medium">
            {subject || "No subject"}
          </Text>
        </Box>
        {preheader && (
          <Text size="sm" color="text.secondary" isTruncated>
            {preheader}
          </Text>
        )}
      </Column>
    </Row>
  );
}

/** The message at a third of its size. Scripts never run in it; it is only read to learn how tall it is. */
function EmailBody({
  title,
  html,
  measuredHeight,
  viewportWidth,
}: {
  title: string;
  html: string;
  measuredHeight?: number;
  viewportWidth?: number;
}) {
  const [measured, setMeasured] = useState(measuredHeight);
  const observerRef = useRef<ResizeObserver | null>(null);
  useEffect(() => () => observerRef.current?.disconnect(), []);
  // The editor's layout (its desktop width and fit sheet), so the height read here is the one the editor draws.
  const layoutWidth = useMemo(
    () => viewportWidth ?? computeDesktopPreviewWidthFromHtml(html),
    [viewportWidth, html],
  );
  // The frame waits for the images' storage keys, so the full-size originals never paint.
  const resized = useResizedEmailHtml(html, EMAIL_TILE_IMAGE_WIDTH);
  const displayHtml = resized.isLoading ? null : resized.html;
  const srcDoc = useMemo(
    () => (displayHtml === null ? null : displayHtml + fitToContentStyle(0)),
    [displayHtml],
  );
  // An email longer than the cap is cut off at its foot.
  const height = Math.min(
    measured ?? CANVAS_EMAIL_FALLBACK_HEIGHT,
    CANVAS_EMAIL_MAX_HEIGHT,
  );
  const scale = CANVAS_NODE_WIDTH / layoutWidth;
  return (
    <Box height={`${Math.ceil(height * scale)}px`} overflow="hidden" bg="white">
      {srcDoc === null ? (
        <Box
          role="status"
          aria-label="Loading…"
          height="100%"
          bg="base.lightBackground"
          // The skeleton's pulse as opacity, which the compositor runs; a color pulse repaints the whole canvas every frame.
          sx={{
            "@keyframes email-tile-pulse": { to: { opacity: 1 } },
          }}
        >
          <Box
            height="100%"
            bg="gray.200"
            opacity={0}
            animation="email-tile-pulse var(--chakra-transition-duration-dramatic) linear infinite alternate"
            _mediaReduceMotion={{ animation: "none", opacity: 0.5 }}
          />
        </Box>
      ) : (
        <chakra.iframe
          title={title}
          srcDoc={srcDoc}
          sandbox="allow-same-origin"
          tabIndex={-1}
          scrolling="no"
          width={`${layoutWidth}px`}
          height={`${height}px`}
          border="none"
          transformOrigin="top left"
          transform={`scale(${scale})`}
          // Read by an opening email's flight, which aims at the editor's fit of this shape.
          {...(measured ? { [EMAIL_HEIGHT_ATTRIBUTE]: measured } : {})}
          onLoad={(event) => {
            const body = event.currentTarget.contentDocument?.body;
            if (!body) return;
            // The body, as the editor measures it: the document never reads shorter than the frame it loaded in.
            const measure = () => {
              const height = resolveContentHeight(
                body.getBoundingClientRect().height,
                body.scrollHeight,
              );
              if (height) setMeasured(height);
            };
            measure();
            // Kept up as fonts and images settle, as the editor's own preview does, so a flight aims at the final height.
            observerRef.current?.disconnect();
            observerRef.current = new ResizeObserver(measure);
            observerRef.current.observe(body);
          }}
        />
      )}
    </Box>
  );
}

/** A shimmering stand-in shaped like the file it will become. */
function FileSkeleton({
  type,
  label,
}: {
  type: AmpLibraryFileFragment["type"];
  label: string;
}) {
  // The ad's shape a step darker than the canvas, with a sheen across it: the skeleton's own pulse repaints the canvas every frame.
  if (type === "ad")
    return (
      <Box
        role="status"
        aria-label={label}
        position="relative"
        width="100%"
        bg="base.backgroundHover"
        borderWidth="hairline"
        borderColor="base.border"
        borderRadius="inherit"
        sx={{ aspectRatio: "1 / 1" }}
      >
        <WorkingSheen />
      </Box>
    );
  return (
    <Box role="status" aria-label={label}>
      <Skeleton isLoading>
        {type === "email" ? (
          <Column bg="white">
            <Row
              gap={2.5}
              px={3.5}
              py={3}
              align="flex-start"
              borderBottomWidth="hairline"
              borderColor="base.divider"
            >
              <SkeletonBox boxSize={7} flexShrink={0} borderRadius="full" />
              <Column flex={1} gap={1.5} pt={0.5}>
                <SkeletonBox height={3} width="40%" />
                <SkeletonBox height={3} width="85%" />
                <SkeletonBox height={3} width="65%" />
              </Column>
            </Row>
            <Column gap={2} p={3.5}>
              <SkeletonBox width="100%" sx={{ aspectRatio: "2 / 1" }} />
              <SkeletonBox height={2.5} width="90%" />
              <SkeletonBox height={2.5} width="75%" />
              <SkeletonBox height={6} width="40%" alignSelf="center" mt={1} />
            </Column>
          </Column>
        ) : type === "doc" ? (
          <Column
            bg="white"
            gap={2}
            p={5}
            sx={{ aspectRatio: DOC_ASPECT_RATIO }}
          >
            <SkeletonBox height={4} width="55%" mb={2} />
            {DOC_LINE_WIDTHS.map((width, index) => (
              <SkeletonBox
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list that never reorders.
                key={index}
                height={2.5}
                width={width}
              />
            ))}
          </Column>
        ) : (
          <SkeletonBox height={48} width="100%" borderRadius="none" />
        )}
      </Skeleton>
    </Box>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return (
    <Row
      bg="base.lightBackground"
      height={48}
      px={4}
      align="center"
      justify="center"
      textAlign="center"
    >
      <Text size="sm" color="text.secondary">
        {children}
      </Text>
    </Row>
  );
}
